<template>
  <VMenu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    :location="'bottom left'"
    content-class="border-thin !rounded-2xl"
    :offset="[25, 30]"
  >
    <template #activator="{ props }">
      <VIcon
        data-testid="date-picker-icon"
        v-bind="props"
        class="material-icons-outlined"
        icon="calendar_today"
        size="20px"
        color="grey-400"
      />
    </template>

    <VDatePicker
      v-model="selectedDate"
      :min="today"
      :max="maxDate"
      :first-day-of-week="1"
      :weekday-format="'short'"
      :show-adjacent-months="true"
      data-testid="range-picker"
      hide-header
      class="range-picker"
      @update:model-value="isMenuOpen = false"
    >
      <template #controls="{ disabled, nextMonth, prevMonth, monthYearText }">
        <VBtn
          :disabled="disabled.includes('prev-month')"
          color="primary"
          icon="$prev"
          @click="prevMonth"
        />

        <p class="font-semibold">
          {{ `${monthYearText.split(' ')[0]} ${monthYearText.split(' ')[1]}` }}
        </p>

        <VBtn
          :disabled="disabled.includes('next-month')"
          color="primary"
          icon="$next"
          @click="nextMonth"
        />
      </template>

      <template #day="{ item, props }">
        <VTooltip
          :location="'right top'"
          :offset="[0, 10]"
          :disabled="isInRange(item.date, { start: today, end: maxDate })"
          :text="
            isDateBefore(item.date, today)
              ? t('Date is in the past')
              : isAfter(item.date, maxDate)
                ? t('Date is too far in the future')
                : undefined
          "
        >
          <template #activator="{ props: tooltipProps }">
            <div v-bind="tooltipProps" :class="['h-full w-full']">
              <VBtn
                v-bind="props"
                :text="item.localized"
                :variant="item.isToday ? 'outlined' : 'text'"
                :rounded="'full'"
                :class="[
                  item.isSelected ? 'bg-primary text-surface' : item.isToday ? 'text-primary' : '',
                  '!h-full !w-full'
                ]"
              />
            </div>
          </template>
        </VTooltip>
      </template>
    </VDatePicker>
  </VMenu>
</template>

<script setup lang="ts">
import { isAfter, isBefore, isWithinInterval } from 'date-fns'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const selectedDate = defineModel<Date>({ default: new Date() })
const isMenuOpen = ref(false)

const today = new Date()

function isDateBefore(date: Date, dateToCompare: Date): boolean {
  const firstDate = new Date(date)
  const secondDate = new Date(dateToCompare)

  firstDate.setHours(0, 0, 0, 0)
  secondDate.setHours(0, 0, 0, 0)

  return isBefore(firstDate, secondDate)
}

function isInRange(date: Date, range: { start: Date; end: Date }): boolean {
  const normalizedDate = new Date(date)
  const normalizedStart = new Date(range.start)
  const normalizedEnd = new Date(range.end)

  normalizedDate.setHours(0, 0, 0, 0)
  normalizedStart.setHours(0, 0, 0, 0)
  normalizedEnd.setHours(23, 59, 59, 0)

  return isWithinInterval(normalizedDate, { start: normalizedStart, end: normalizedEnd })
}

const maxDate = computed(() => {
  const result = new Date(today)
  result.setDate(today.getDate() + 2)

  return result
})
</script>

<style scoped>
.range-picker {
  padding: 20px 24px;
}

:deep(.range-picker .v-picker__body) {
  flex-wrap: nowrap;

  .v-date-picker-controls {
    padding: 0;
  }

  .v-date-picker-month {
    padding: 0;

    .v-date-picker-month__days {
      column-gap: 0;
      row-gap: 4px;
    }
  }
}
</style>
